import { useEffect, useState } from 'react';
import { AlertCircle, Loader2, Plus, Trash2, User as UserIcon, X } from 'lucide-react';
import { SERVICE_RESPONSABLE_FONCTIONS, type ServiceResponsableFonction } from '@mygmao/shared';
import { api, extractErrorMessage } from '../../lib/api';
import { ModalPortal } from '../ModalPortal';
import { usePermissions } from '../../hooks/usePermissions';

/**
 * Panel responsables d'un service (US-SVC-04). Liste titulaires + suppléants
 * + historique. Bouton "+ Ajouter" ouvre un mini-modal.
 */

interface ResponsableRow {
  id: string;
  service_id: string;
  utilisateur_id: string;
  fonction: ServiceResponsableFonction;
  est_titulaire: boolean;
  date_debut: string;
  date_fin: string | null;
  utilisateur: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  } | null;
}

interface PersonnelOption {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

const FONCTION_LABELS: Record<ServiceResponsableFonction, string> = {
  chef_service: 'Chef de service',
  responsable_operationnel: 'Responsable opérationnel',
  cadre_superieur: 'Cadre supérieur',
  referent_maintenance: 'Référent maintenance',
  correspondant_gmao: 'Correspondant GMAO',
  technicien_referent: 'Technicien référent',
  assistant_service: 'Assistant de service',
};

export function ResponsablesPanel({ serviceId }: { serviceId: string }) {
  const { can } = usePermissions();
  const canEdit = can('services', 'update');

  const [items, setItems] = useState<ResponsableRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const res = await api.get<{ data: ResponsableRow[] }>(
        `/services/${serviceId}/responsables?include_history=true`,
      );
      setItems(res.data.data);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId]);

  async function handleDelete(id: string) {
    if (!confirm('Supprimer ce responsable ?')) return;
    try {
      await api.delete(`/services/responsables/${id}`);
      refresh();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  }

  const today = new Date().toISOString().slice(0, 10);
  const isActive = (r: ResponsableRow) => !r.date_fin || r.date_fin >= today;

  const titulaires = items.filter((r) => isActive(r) && r.est_titulaire);
  const suppleants = items.filter((r) => isActive(r) && !r.est_titulaire);
  const historique = items.filter((r) => !isActive(r));

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      {canEdit && (
        <div className="flex justify-end">
          <button
            onClick={() => setAddOpen(true)}
            className="inline-flex items-center px-3 py-1.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm font-medium"
          >
            <Plus className="w-4 h-4 mr-1" />
            Ajouter un responsable
          </button>
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-sm text-gray-500 italic">Aucun responsable affecté.</p>
      ) : (
        <>
          {titulaires.length > 0 && (
            <Section title="Titulaires">
              {titulaires.map((r) => (
                <Row key={r.id} row={r} canEdit={canEdit} onDelete={() => handleDelete(r.id)} />
              ))}
            </Section>
          )}
          {suppleants.length > 0 && (
            <Section title="Suppléants">
              {suppleants.map((r) => (
                <Row key={r.id} row={r} canEdit={canEdit} onDelete={() => handleDelete(r.id)} />
              ))}
            </Section>
          )}
          {historique.length > 0 && (
            <Section title="Historique">
              <div className="opacity-60">
                {historique.map((r) => (
                  <Row key={r.id} row={r} canEdit={canEdit} onDelete={() => handleDelete(r.id)} />
                ))}
              </div>
            </Section>
          )}
        </>
      )}

      {addOpen && (
        <AddResponsableModal
          serviceId={serviceId}
          onClose={() => setAddOpen(false)}
          onSaved={() => {
            setAddOpen(false);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{title}</h4>
      <ul className="space-y-1.5">{children}</ul>
    </section>
  );
}

function Row({
  row,
  canEdit,
  onDelete,
}: {
  row: ResponsableRow;
  canEdit: boolean;
  onDelete(): void;
}) {
  const nom = row.utilisateur
    ? `${row.utilisateur.first_name} ${row.utilisateur.last_name}`
    : 'Utilisateur supprimé';
  return (
    <li className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 px-3 py-2 bg-white">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900 flex items-center">
          <UserIcon className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
          {nom}
          {row.est_titulaire && (
            <span className="ml-2 inline-flex items-center text-xs px-1.5 py-0.5 rounded font-medium border bg-green-100 text-green-800 border-green-200">
              Titulaire
            </span>
          )}
        </p>
        <p className="text-xs text-gray-500 mt-0.5">
          {FONCTION_LABELS[row.fonction]} · {new Date(row.date_debut).toLocaleDateString()} →{' '}
          {row.date_fin ? new Date(row.date_fin).toLocaleDateString() : '—'}
        </p>
      </div>
      {canEdit && (
        <button
          onClick={onDelete}
          className="p-1.5 rounded text-red-600 hover:bg-red-50"
          aria-label="Supprimer"
          title="Supprimer"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </li>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Add modal
// ─────────────────────────────────────────────────────────────────────────────

function AddResponsableModal({
  serviceId,
  onClose,
  onSaved,
}: {
  serviceId: string;
  onClose(): void;
  onSaved(): void;
}) {
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [personnels, setPersonnels] = useState<PersonnelOption[]>([]);
  const [loadingPersonnels, setLoadingPersonnels] = useState(true);

  const [utilisateurId, setUtilisateurId] = useState('');
  const [fonction, setFonction] = useState<ServiceResponsableFonction>('chef_service');
  const [dateDebut, setDateDebut] = useState(new Date().toISOString().slice(0, 10));
  const [dateFin, setDateFin] = useState('');
  const [estTitulaire, setEstTitulaire] = useState(true);

  useEffect(() => {
    api
      .get<{ data: PersonnelOption[] }>('/personnels?status=active&page_size=200')
      .then((res) => setPersonnels(res.data.data))
      .catch(() => {
        // Silencieux : champ select restera vide.
      })
      .finally(() => setLoadingPersonnels(false));
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!utilisateurId) {
      setErrorMessage('Sélectionnez un utilisateur.');
      return;
    }
    setSaving(true);
    setErrorMessage(null);
    try {
      await api.post(`/services/${serviceId}/responsables`, {
        utilisateur_id: utilisateurId,
        fonction,
        est_titulaire: estTitulaire,
        date_debut: dateDebut,
        date_fin: dateFin || null,
      });
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-xl max-w-md w-full shadow-2xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
            <h3 className="text-lg font-semibold text-gray-900">Ajouter un responsable</h3>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-3">
            {errorMessage && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Utilisateur *</label>
              <select
                value={utilisateurId}
                onChange={(e) => setUtilisateurId(e.target.value)}
                disabled={loadingPersonnels}
                className={inputClass}
                required
              >
                <option value="">— Sélectionnez —</option>
                {personnels.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.first_name} {p.last_name} ({p.email})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fonction *</label>
              <select
                value={fonction}
                onChange={(e) => setFonction(e.target.value as ServiceResponsableFonction)}
                className={inputClass}
              >
                {SERVICE_RESPONSABLE_FONCTIONS.map((f) => (
                  <option key={f} value={f}>
                    {FONCTION_LABELS[f]}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date début *</label>
                <input
                  type="date"
                  value={dateDebut}
                  onChange={(e) => setDateDebut(e.target.value)}
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date fin</label>
                <input
                  type="date"
                  value={dateFin}
                  onChange={(e) => setDateFin(e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>

            <label className="flex items-center space-x-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={estTitulaire}
                onChange={(e) => setEstTitulaire(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span>Titulaire (un seul actif par fonction — l'ancien devient suppléant)</span>
            </label>
          </form>

          <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Ajouter
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500';
